import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  cancelStripeSubscription,
  getUserOrganization,
  listUserStripeCharges,
  listUserStripeInvoices,
  listUserStripeSubscriptions,
  refundStripeInvoice,
  OrganizationContext,
  StripeCharge,
  StripeInvoice,
  StripeList,
  StripeSubscription,
} from "@/services/subscriptionsApi";
import { tierBadgeColor, tierBadgeLabel } from "@/lib/tierBadge";
import { roleBadgeColor, roleBadgeLabel } from "@/lib/roleBadge";
import ExecutionAccountPanel from "./ExecutionAccountPanel";

function formatAmount(amount?: number | null, currency?: string | null) {
  if (amount == null) return "-";
  const value = amount / 100;
  return `${value.toFixed(2)} ${(currency || "eur").toUpperCase()}`;
}

function formatDate(iso?: string | null) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    trialing: "bg-blue-100 text-blue-800",
    canceled: "bg-gray-100 text-gray-700",
    incomplete: "bg-yellow-100 text-yellow-800",
    incomplete_expired: "bg-red-100 text-red-800",
    past_due: "bg-orange-100 text-orange-800",
    unpaid: "bg-red-100 text-red-800",
    paid: "bg-green-100 text-green-800",
    open: "bg-blue-100 text-blue-800",
    draft: "bg-gray-100 text-gray-700",
    void: "bg-gray-100 text-gray-700",
    uncollectible: "bg-red-100 text-red-800",
    succeeded: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    failed: "bg-red-100 text-red-800",
  };
  const cls = colors[status.toLowerCase()] || "bg-gray-100 text-gray-700";
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}

// One Stripe list of the page, loaded on its own: a failing list (or a Stripe
// customer unknown to the current mode) no longer blanks the other two.
type Section<T> = {
  items: T[];
  error: string | null;
  customerMissing: boolean;
  noCustomer: boolean;
};

function emptySection<T>(): Section<T> {
  return { items: [], error: null, customerMissing: false, noCustomer: false };
}

function toSection<T>(result: PromiseSettledResult<StripeList<T>>): Section<T> {
  if (result.status === "rejected") {
    const reason: unknown = result.reason;
    return {
      ...emptySection<T>(),
      error: reason instanceof Error ? reason.message : "Erreur de chargement",
    };
  }
  return {
    items: result.value.items ?? [],
    error: null,
    customerMissing: !!result.value.customer_missing_in_current_mode,
    noCustomer: !!result.value.no_customer,
  };
}

function SectionError({ label, error }: { label: string; error: string | null }) {
  if (!error) return null;
  return (
    <div className="mb-2 text-sm text-red-600">
      {label} : {error}
    </div>
  );
}

export default function SubscriptionDetailPage() {
  const navigate = useNavigate();
  const { userId = "" } = useParams();
  const { toast } = useToast();
  const [subs, setSubs] = useState<Section<StripeSubscription>>(emptySection());
  const [invoices, setInvoices] = useState<Section<StripeInvoice>>(emptySection());
  const [charges, setCharges] = useState<Section<StripeCharge>>(emptySection());
  const [org, setOrg] = useState<OrganizationContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [s, i, c, o] = await Promise.allSettled([
      listUserStripeSubscriptions(navigate, userId),
      listUserStripeInvoices(navigate, userId),
      listUserStripeCharges(navigate, userId),
      // Best-effort : l'org ne doit pas bloquer l'affichage du billing Stripe.
      getUserOrganization(navigate, userId),
    ]);
    setSubs(toSection(s));
    setInvoices(toSection(i));
    setCharges(toSection(c));
    setOrg(o.status === "fulfilled" ? o.value : null);
    setLoading(false);
  };

  const sections = [subs, invoices, charges];
  const customerMissing = sections.some((s) => s.customerMissing);
  const noCustomer = sections.every((s) => s.noCustomer);

  useEffect(() => {
    if (!userId) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const onCancel = async (subId: string) => {
    setActionInFlight(subId);
    try {
      await cancelStripeSubscription(navigate, userId, subId);
      toast({ title: "Subscription annulee", description: subId });
      await load();
    } catch (e) {
      toast({ title: "Echec", description: e instanceof Error ? e.message : "Erreur", variant: "destructive" });
    } finally {
      setActionInFlight(null);
    }
  };

  const onRefund = async (invoiceId: string) => {
    setActionInFlight(invoiceId);
    try {
      await refundStripeInvoice(navigate, userId, invoiceId);
      toast({ title: "Facture remboursee", description: invoiceId });
      await load();
    } catch (e) {
      toast({ title: "Echec", description: e instanceof Error ? e.message : "Erreur", variant: "destructive" });
    } finally {
      setActionInFlight(null);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Link to="/subscriptions" className="text-sm text-gray-600 hover:underline">
              Abonnements
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <h1 className="font-mono text-sm">{userId.substring(0, 12)}...</h1>
          </div>
        </header>

        <div className="space-y-8 p-6">
          {loading && <div className="text-sm text-gray-500">Chargement...</div>}
          {!loading && customerMissing && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Le client Stripe enregistré pour ce compte n&apos;existe pas dans le mode Stripe actuel
              (clé test ou live) : aucune donnée Stripe à afficher. Il date probablement d&apos;avant
              la bascule test → live.
            </div>
          )}
          {!loading && noCustomer && (
            <div className="text-sm text-gray-500">Aucun client Stripe pour ce compte.</div>
          )}

          {org && (
            <section>
              <h2 className="mb-3 text-lg font-semibold">Organisation B2B</h2>
              <div className="rounded border p-4">
                <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-xs uppercase text-gray-500">Organisation</dt>
                    <dd className="font-medium">
                      <Link to={`/organizations/${org.id}`} className="text-blue-600 hover:underline">
                        {org.name}
                      </Link>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-gray-500">Tier</dt>
                    <dd>
                      {org.is_business && org.tier ? (
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${tierBadgeColor(org.tier)}`}>
                          {tierBadgeLabel(org.tier)}
                        </span>
                      ) : (
                        <span className="text-gray-500">Compte personnel</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-gray-500">Rôle</dt>
                    <dd>
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${roleBadgeColor(org.member_role)}`}>
                        {roleBadgeLabel(org.member_role)}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-gray-500">Sièges</dt>
                    <dd>
                      {org.active_seats ?? "-"} / {org.purchased_seats ?? "-"} actifs
                    </dd>
                  </div>
                </dl>
              </div>
            </section>
          )}

          <section>
            <h2 className="mb-3 text-lg font-semibold">Compte d&apos;exécution</h2>
            <ExecutionAccountPanel userId={userId} />
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold">Subscriptions Stripe</h2>
            <SectionError label="Abonnements" error={subs.error} />
            <div className="overflow-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Prix</th>
                    <th className="px-3 py-2 text-left">Intervalle</th>
                    <th className="px-3 py-2 text-left">Creee</th>
                    <th className="px-3 py-2 text-left">Fin periode</th>
                    <th className="px-3 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.items.map((s) => (
                    <tr key={s.id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{s.id}</td>
                      <td className="px-3 py-2">{statusBadge(s.status)}</td>
                      <td className="px-3 py-2">{formatAmount(s.amount, s.currency)}</td>
                      <td className="px-3 py-2">
                        {s.interval_count && s.interval ? `${s.interval_count} ${s.interval}` : "-"}
                      </td>
                      <td className="px-3 py-2">{formatDate(s.created)}</td>
                      <td className="px-3 py-2">{formatDate(s.current_period_end)}</td>
                      <td className="px-3 py-2">
                        {s.status === "active" || s.status === "trialing" || s.status === "past_due" ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm" disabled={actionInFlight === s.id}>
                                Annuler
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Annuler cette subscription ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Annulation immediate avec prorata. L'action est irreversible cote Stripe.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Non</AlertDialogCancel>
                                <AlertDialogAction onClick={() => onCancel(s.id)}>Oui, annuler</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!subs.items.length && !loading && (
                    <tr>
                      <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                        Aucune subscription
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold">Factures</h2>
            <SectionError label="Factures" error={invoices.error} />
            <div className="overflow-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Numero</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Montant paye</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Subscription</th>
                    <th className="px-3 py-2 text-left">PDF</th>
                    <th className="px-3 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.items.map((inv) => (
                    <tr key={inv.id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{inv.number || inv.id}</td>
                      <td className="px-3 py-2">{statusBadge(inv.status)}</td>
                      <td className="px-3 py-2">{formatAmount(inv.amount_paid, inv.currency)}</td>
                      <td className="px-3 py-2">{formatDate(inv.created)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{inv.subscription || "-"}</td>
                      <td className="px-3 py-2">
                        {inv.invoice_pdf ? (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            Voir
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {inv.status === "paid" && inv.charge ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" disabled={actionInFlight === inv.id}>
                                Rembourser
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Rembourser la facture ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Remboursement total ({formatAmount(inv.amount_paid, inv.currency)}).
                                  Action irreversible cote Stripe.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Non</AlertDialogCancel>
                                <AlertDialogAction onClick={() => onRefund(inv.id)}>
                                  Oui, rembourser
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!invoices.items.length && !loading && (
                    <tr>
                      <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                        Aucune facture
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold">Transactions</h2>
            <SectionError label="Transactions" error={charges.error} />
            <div className="overflow-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Montant</th>
                    <th className="px-3 py-2 text-left">Rembourse</th>
                    <th className="px-3 py-2 text-left">Carte</th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Facture</th>
                    <th className="px-3 py-2 text-left">Recu</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.items.map((ch) => (
                    <tr key={ch.id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{ch.id}</td>
                      <td className="px-3 py-2">
                        {statusBadge(ch.status)}
                        {ch.failure_message ? (
                          <div className="mt-1 text-xs text-red-600">{ch.failure_message}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">{formatAmount(ch.amount, ch.currency)}</td>
                      <td className="px-3 py-2">
                        {ch.amount_refunded
                          ? formatAmount(ch.amount_refunded, ch.currency)
                          : "-"}
                      </td>
                      <td className="px-3 py-2">
                        {ch.card_brand && ch.card_last4
                          ? `${ch.card_brand} •••• ${ch.card_last4}`
                          : "-"}
                      </td>
                      <td className="px-3 py-2">{formatDate(ch.created)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{ch.invoice || "-"}</td>
                      <td className="px-3 py-2">
                        {ch.receipt_url ? (
                          <a
                            href={ch.receipt_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            Voir
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                  {!charges.items.length && !loading && (
                    <tr>
                      <td className="px-3 py-4 text-center text-gray-500" colSpan={8}>
                        Aucune transaction
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
